import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Lock, ShieldCheck, Sparkles, Check, Heart } from "lucide-react";

const INCLUDES = [
  "18+ deeply researched plant profiles (and growing)",
  "Foraging safety + every toxic lookalike to know",
  "Recipes & preparation methods (teas, tinctures, poultices)",
  "Detox protocols sequencing mobilizers + binders correctly",
  "Categorized by goal: weight, detox, cognition, wellness",
  "Lifetime access — no subscription, no upsells",
];

const PRESETS = [
  { value: 4, label: "$4", caption: "the price of a coffee" },
  { value: 7, label: "$7", caption: "a kind tip" },
  { value: 12, label: "$12", caption: "the price of a book" },
  { value: 20, label: "$20", caption: "the price of a meal" },
];

const FLOOR = 4;
const CEILING = 100;

export default function Unlock() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [amount, setAmount] = useState(4);

  useEffect(() => {
    if (!loading && user && user.is_paid) nav("/library");
  }, [user, loading, nav]);

  const isTip = amount > FLOOR;
  const caption = useMemo(() => {
    if (amount <= 4) return "Pay what feels fair (min $4).";
    if (amount < 8) return "A small tip on top of the $4.";
    if (amount < 15) return "Generous — this is how the library stays free of ads.";
    if (amount < 25) return "Wonderful — you're helping someone forage for free.";
    return "Extraordinary. Thank you for keeping this work alive.";
  }, [amount]);

  const handlePay = async () => {
    setError("");
    if (!user) {
      nav(`/register?next=/unlock`);
      return;
    }
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", { origin_url: origin, amount });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-7">
          <p className="label-eyebrow">One-time unlock</p>
          <h1 className="font-serif text-5xl md:text-6xl mt-3 leading-[1.05]">
            $4. Once.<br />
            <span className="italic text-[var(--forest)]">For the whole library.</span>
          </h1>
          <hr className="divider-rule" />
          <p className="text-lg text-[var(--ink-soft)] leading-relaxed">
            We chose $4 because the field guide should cost about as much as
            the coffee you drink while reading it. Buy it once, keep it for
            life. We do not sell your email, we do not upsell you to a
            membership, we do not run ads.
          </p>

          <ul className="mt-10 space-y-3">
            {INCLUDES.map((line) => (
              <li key={line} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[var(--forest)] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                <span className="text-[var(--ink)]">{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <aside className="md:col-span-5 card-journal">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-[var(--terracotta)]" strokeWidth={1.5} />
            <p className="label-eyebrow">The Wild Vitality Field Guide</p>
          </div>

          {/* Amount picker */}
          <div className="mt-5">
            <div className="flex items-end gap-2">
              <span className="font-serif text-6xl text-[var(--forest)]" data-testid="unlock-amount-display">${amount}</span>
              <span className="font-mono text-xs text-[var(--muted)] pb-3">USD · one-time</span>
            </div>
            <p className="text-xs text-[var(--ink-soft)] mt-1 min-h-[1.25rem]" data-testid="unlock-amount-caption">
              {caption}
            </p>

            <div className="mt-5 grid grid-cols-4 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.value}
                  data-testid={`unlock-preset-${p.value}`}
                  type="button"
                  onClick={() => setAmount(p.value)}
                  className={`py-2 px-2 text-sm border transition-colors rounded-sm font-medium ${
                    amount === p.value
                      ? "bg-[var(--forest)] text-[var(--paper)] border-[var(--forest)]"
                      : "border-[var(--border-line)] text-[var(--ink)] hover:bg-[var(--paper-deep)]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="mt-5">
              <label className="label-eyebrow flex items-center gap-1.5">
                <Heart className="w-3 h-3 text-[var(--terracotta)]" /> Slide if you want to tip
              </label>
              <input
                data-testid="unlock-amount-slider"
                type="range"
                min={FLOOR}
                max={CEILING}
                step={1}
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value, 10))}
                className="w-full mt-3 accent-[var(--forest)]"
              />
              <div className="flex justify-between font-mono text-xs text-[var(--muted)] mt-1">
                <span>${FLOOR}</span>
                <span>${CEILING}</span>
              </div>
            </div>
          </div>

          <hr className="divider-rule" />

          {user && !user.is_paid ? (
            <button
              data-testid="unlock-pay-btn"
              onClick={handlePay}
              disabled={busy}
              className="btn-accent w-full justify-center"
            >
              {busy ? (
                "Redirecting to Stripe…"
              ) : (
                <>
                  <Sparkles className="w-4 h-4" /> Pay ${amount} & unlock
                </>
              )}
            </button>
          ) : !user ? (
            <div className="space-y-3">
              <Link data-testid="unlock-register-cta" to="/register?next=/unlock" className="btn-accent w-full justify-center">
                Create account · from $4
              </Link>
              <Link data-testid="unlock-login-link" to="/login?next=/unlock" className="block text-center text-sm btn-ghost">
                Already have an account? Sign in
              </Link>
            </div>
          ) : (
            <Link to="/library" className="btn-primary w-full justify-center">Open library</Link>
          )}

          {error && (
            <p data-testid="unlock-error" className="text-sm text-[var(--terracotta)] mt-4">
              {error}
            </p>
          )}

          <p className="text-xs text-[var(--muted)] mt-5 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure Stripe checkout. Cards & wallets.
          </p>

          {isTip && (
            <p className="text-xs text-[var(--ink-soft)] mt-3 italic">
              Anything over $4 keeps the library ad-free and helps us add new plant profiles. Thank you.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
